import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { AdminRole, PageKey, useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { createAdminAccess, updateAdminAccess } from "../../common/requests/admins/endpoints";
import type { AdminUserRow } from "../../common/requests/users/endpoints";
import { errorMessage, useIsNarrow, useToast } from "../../ui";
import { PERMISSION_GROUPS, groupKeys, toggleGroupPermissions } from "./userData";

interface AdminAccessDialogProps {
  open: boolean;
  user: AdminUserRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Grant (no admin_user_id yet) or edit a user's dashboard role and pages. */
export default function AdminAccessDialog({ open, user, onClose, onSaved }: AdminAccessDialogProps) {
  const { idToken } = useAuth();
  const toast = useToast();
  const narrow = useIsNarrow(600);
  const editing = !!user?.admin_user_id;
  const [userId, setUserId] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<AdminRole>("member");
  const [permissions, setPermissions] = React.useState<PageKey[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !user) return;
    setUserId(user.user_id || "");
    setEmail(user.email || "");
    setRole(((user.admin_user_id && user.role) as AdminRole) || "member");
    setPermissions(user.admin_user_id ? ((user.permissions || []) as PageKey[]) : []);
    setError(null);
  }, [open, user]);

  const toggle = (key: PageKey) => setPermissions((prev) => (prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]));

  const submit = async () => {
    if (!idToken || !user) return;
    setSaving(true);
    setError(null);
    const body = {
      display_name: user.display_name || null,
      role,
      permissions: role === "superadmin" ? [] : permissions,
    };
    try {
      if (editing) {
        await updateAdminAccess(user.admin_user_id!, body, idToken);
        toast("Admin access updated");
      } else {
        await createAdminAccess({ ...body, user_id: userId.trim(), email: email.trim() }, idToken);
        toast("Admin access granted");
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Couldn't save admin access."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={narrow} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? "Edit admin access" : "Grant admin access"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={editing} fullWidth helperText="Must match users.user_id" />
        <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={editing} fullWidth type="email" />
        <TextField label="Display name" value={user?.display_name || ""} fullWidth disabled helperText="Comes from the user's marketplace username" />
        <FormControl fullWidth>
          <InputLabel id="admin-role-label">Role</InputLabel>
          <Select labelId="admin-role-label" value={role} label="Role" onChange={(e) => setRole(e.target.value as AdminRole)}>
            <MenuItem value="superadmin">Superadmin</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="member">Member</MenuItem>
          </Select>
          <FormHelperText>
            Superadmins see every page and action. Admins and Members (same access today) see only the pages ticked below.
          </FormHelperText>
        </FormControl>

        {role !== "superadmin" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Typography variant="overline" sx={{ color: C.muted }}>
              Page permissions
            </Typography>
            {PERMISSION_GROUPS.map((group) => {
              const keys = groupKeys(group);
              const count = keys.filter((k) => permissions.includes(k)).length;
              return (
                <Box key={group.label} sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", px: 1.25, py: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={count === keys.length}
                        indeterminate={count > 0 && count < keys.length}
                        onChange={() => setPermissions((prev) => toggleGroupPermissions(prev, keys))}
                      />
                    }
                    label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>{group.label}</Typography>}
                    sx={{ display: "flex", minHeight: 44 }}
                  />
                  <Box sx={{ pl: 3.5 }}>
                    {group.parent && (
                      <FormControlLabel
                        control={<Checkbox checked={permissions.includes(group.parent)} onChange={() => toggle(group.parent!)} />}
                        label={group.parentLabel}
                        sx={{ display: "flex", minHeight: 44 }}
                      />
                    )}
                    {group.children.map(({ key, label }) => (
                      <FormControlLabel
                        key={key}
                        control={<Checkbox checked={permissions.includes(key)} onChange={() => toggle(key)} />}
                        label={label}
                        sx={{ display: "flex", minHeight: 44 }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1, "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" } } }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || (!editing && (!email.trim() || !userId.trim()))}>
          {saving ? "Saving…" : editing ? "Save" : "Grant access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
